import { useEffect, useRef } from 'react';

// Move keyboard + screen-reader focus to a container's primary
// heading on every sub-screen change. Works with onboarding flows
// (FoundationLesson, PathAssessment, OnboardingFlow top-level steps)
// so advancing past step N announces step N+1's heading instead of
// leaving focus stranded on the CTA the user just pressed.
//
// Usage
//   const scrollRef = useRef(null);
//   useScreenFocus(scrollRef, currentScreen);
//
// Behaviour
//   - Finds the first `<h1>` (or element with data-screen-title) inside
//     the ref on every screenIndex change.
//   - Applies tabIndex="-1" so the heading is programmatically focusable
//     without becoming a natural Tab stop, then calls focus({
//     preventScroll: true }) so mobile viewports don't jump.
//   - Skips focusing when the user is in a text input (Email, Code,
//     delete-confirm), because stealing focus there is disruptive.

export default function useScreenFocus(containerRef, screenIndex) {
  const lastIndex = useRef(null);

  useEffect(() => {
    if (lastIndex.current === screenIndex) return;
    lastIndex.current = screenIndex;
    const container = containerRef.current;
    if (!container) return;

    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (active && /^(input|textarea|select)$/i.test(active.tagName)) return;

    const heading =
      container.querySelector('[data-screen-title]') ||
      container.querySelector('h1') ||
      container.querySelector('h2');

    if (!heading) return;
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    try {
      heading.focus({ preventScroll: true });
    } catch {
      try { heading.focus(); } catch { /* jsdom */ }
    }
  }, [containerRef, screenIndex]);
}
